<template>
  <li
    v-if="item.visible !== false"
    :class="{
      'layout-root-menuitem': root,
      'active-menuitem': expanded,
      'contains-active-route': containsActiveRoute(item)
    }"
  >
    <div v-if="root" class="layout-menuitem-root-text">{{ item.label }}</div>

    <button
      v-if="!root && item.items"
      type="button"
      class="layout-menuitem-link layout-menuitem-group"
      :aria-expanded="expanded"
      @click="toggleGroup"
    >
      <span class="layout-menuitem-icon">
        <FontAwesomeIcon :icon="['fas', item.icon || 'list']" />
      </span>
      <span class="layout-menuitem-copy">
        <span class="layout-menuitem-text">{{ item.label }}</span>
      </span>
      <span v-if="item.badge" class="layout-menuitem-badge">{{ item.badge }}</span>
      <i class="pi pi-chevron-right layout-submenu-toggler" />
    </button>

    <router-link
      v-else-if="!root && item.to"
      :to="item.to"
      class="layout-menuitem-link"
      :class="{ 'active-route': checkActiveRoute(item) }"
      :aria-current="checkActiveRoute(item) ? 'page' : undefined"
      @click="selectLeaf"
    >
      <span class="layout-menuitem-icon">
        <FontAwesomeIcon :icon="['fas', item.icon || 'home']" />
      </span>
      <span class="layout-menuitem-copy">
        <span class="layout-menuitem-text">{{ item.label }}</span>
      </span>
      <span v-if="item.badge" class="layout-menuitem-badge">{{ item.badge }}</span>
    </router-link>

    <a
      v-else-if="!root && item.url"
      :href="item.url"
      class="layout-menuitem-link"
      :target="item.target"
      @click="selectLeaf"
    >
      <span class="layout-menuitem-icon">
        <FontAwesomeIcon :icon="['fas', item.icon || 'home']" />
      </span>
      <span class="layout-menuitem-copy">
        <span class="layout-menuitem-text">{{ item.label }}</span>
      </span>
    </a>

    <ul v-if="root && item.items" class="layout-submenu">
      <AppMenuItem
        v-for="(child, childIndex) in item.items"
        :key="`${child.label}-${childIndex}`"
        :index="childIndex"
        :item="child"
        :parent-item-key="itemKey"
        :root="false"
      />
    </ul>

    <Transition v-else-if="item.items" name="layout-submenu">
      <ul v-show="expanded" class="layout-submenu">
        <AppMenuItem
          v-for="(child, childIndex) in item.items"
          :key="`${child.label}-${childIndex}`"
          :index="childIndex"
          :item="child"
          :parent-item-key="itemKey"
          :root="false"
        />
      </ul>
    </Transition>
  </li>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLayout } from '@/layout/composables/layout';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  index: {
    type: Number,
    default: 0
  },
  root: {
    type: Boolean,
    default: true
  },
  parentItemKey: {
    type: String,
    default: null
  }
});

const route = useRoute();
const router = useRouter();
const { layoutConfig, layoutState, setActiveMenuItem, onMenuToggle } = useLayout();
const expanded = ref(false);
const itemKey = computed(() => (props.parentItemKey ? `${props.parentItemKey}-${props.index}` : String(props.index)));

const resolveTarget = (to) => {
  if (!to) return null;
  try {
    return router.resolve(to);
  } catch {
    return null;
  }
};

const checkActiveRoute = (item) => {
  const target = resolveTarget(item.to);
  if (!target) return false;
  const hasQuery = Object.keys(target.query || {}).length > 0;
  return hasQuery ? target.fullPath === route.fullPath : target.path === route.path;
};

const containsActiveRoute = (item) => {
  if (item.to && checkActiveRoute(item)) return true;
  return item.items?.some((child) => containsActiveRoute(child)) || false;
};

const syncExpandedWithRoute = () => {
  if (props.item.items) expanded.value = containsActiveRoute(props.item);
};

const toggleGroup = () => {
  if (props.item.disabled) return;
  expanded.value = !expanded.value;
  setActiveMenuItem(expanded.value ? itemKey.value : props.parentItemKey);

  if (props.item.command) {
    props.item.command({ item: props.item });
  }
};

const selectLeaf = (event) => {
  if (props.item.disabled) {
    event.preventDefault();
    return;
  }

  setActiveMenuItem(itemKey.value);
  if (layoutState.staticMenuMobileActive.value || layoutState.overlayMenuActive.value) {
    onMenuToggle();
  }

  if (props.item.command) {
    props.item.command({ originalEvent: event, item: props.item });
  }
};

onBeforeMount(syncExpandedWithRoute);
watch(layoutConfig.activeMenuItem, (activeKey) => {
  if (!props.item.items || activeKey === null) return;
  expanded.value = activeKey === itemKey.value || String(activeKey).startsWith(`${itemKey.value}-`);
});
watch(() => route.fullPath, syncExpandedWithRoute);
</script>
